import { useCallback, useMemo, useState } from 'react'

import {
  arrivalSound,
  CHIME_CHOICE_STORAGE_KEY,
  CHIME_STORAGE_KEY,
  chimeEnabled,
  chimeSound,
  chosenArrivalSound,
  storedChimeValue,
  type ArrivalSoundId,
  type ChimeKind,
} from '../arrival-chime'
import { useChimeOutput } from '../chime-player'

/** What a sound picker needs from the chime - §9.676. */
export interface ArrivalSoundControls {
  /** The sound an ordinary arrival plays on this device. */
  choice: ArrivalSoundId
  /** Keeps `id` for this device and plays it, so a choice is never made blind. */
  choose: (id: ArrivalSoundId) => void
  /** Plays `id` once, whether or not sound is switched on - a sound is chosen before it is switched on. */
  preview: (id: ArrivalSoundId) => void
}

function readStored(key: string): string | null {
  try {
    return localStorage.getItem(key)
  } catch {
    return null
  }
}

function writeStored(key: string, value: string): void {
  try {
    localStorage.setItem(key, value)
  } catch {
    // A browser refusing storage still keeps the answer for this session.
  }
}

/**
 * Plays the arrival chime, and remembers whether this device wants it and which sound - §9.110, §9.676.
 *
 * <p>The `AudioContext` and the oscillators are `useChimeOutput`'s (§9.675), shared with the ready bell, so the
 * two cannot come to play the same sound in different ways. Its two rules hold here too: one context, created on
 * the first sound, and a failure is silent.
 *
 * <p><b>Two answers per device, under two keys.</b> Whether it sounds (`agos_arrival_sound`, off until pressed -
 * 2026-08-12) and which sound an ordinary arrival makes (`agos_arrival_sound_choice`, legacy's chime until chosen -
 * #122). The urgent pattern is not a choice: an unaccepted web order sounds the same on every device.
 *
 * <p><b>The returned object is stable</b> while nothing in it changes, so a screen that lists it in an effect's
 * dependencies re-runs that effect when the switch or the choice moves rather than on every render.
 */
export function useArrivalChime(): {
  enabled: boolean
  toggle: () => void
  play: (kind: ChimeKind) => void
} & ArrivalSoundControls {
  const [enabled, setEnabled] = useState(() => chimeEnabled(readStored(CHIME_STORAGE_KEY)))
  const [choice, setChoice] = useState(() => chosenArrivalSound(readStored(CHIME_CHOICE_STORAGE_KEY)))
  const output = useChimeOutput()

  const play = useCallback(
    (kind: ChimeKind) => {
      if (!enabled) return
      output(chimeSound(kind, choice))
    },
    [enabled, choice, output],
  )

  const toggle = useCallback(() => {
    setEnabled((current) => {
      const next = !current
      writeStored(CHIME_STORAGE_KEY, storedChimeValue(next))
      return next
    })
  }, [])

  const preview = useCallback((id: ArrivalSoundId) => output(arrivalSound(id)), [output])

  const choose = useCallback(
    (id: ArrivalSoundId) => {
      writeStored(CHIME_CHOICE_STORAGE_KEY, id)
      setChoice(id)
      output(arrivalSound(id))
    },
    [output],
  )

  return useMemo(
    () => ({ enabled, toggle, play, choice, choose, preview }),
    [enabled, toggle, play, choice, choose, preview],
  )
}
